import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { API } from '@/App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Calculator, ArrowLeft, Clock, DollarSign } from 'lucide-react';

const ChitCalculatorPage = () => {
  const [formData, setFormData] = useState({
    base_rate: 250,
    call_time: '07:00',
    wrap_time: '19:00',
    lunch_break: true,
    early_call: false,
    night_shoot: false,
    holiday_pay_percent: 12.07
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const calculateChit = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/chit/calculate`, formData);
      setResult(response.data);
    } catch (error) {
      toast.error('Failed to calculate');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="bg-[#121212] border-b border-white/5">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_set-roster/artifacts/4vngv49q_2ndTeamStandinsLogoWhite.png" 
              alt="2nd Team Casting" 
              className="h-28 w-auto"
            />
          </Link>
          <Link to="/" className="text-white/60 hover:text-white flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-6 py-12">
        <div className="text-center mb-12">
          <h1 className="font-['Playfair_Display'] text-4xl md:text-5xl font-bold text-white mb-4">
            <span className="gold-text">Chit Calculator</span>
          </h1>
          <p className="text-white/60 text-lg max-w-2xl mx-auto">
            Calculate stand-in pay based on UK film industry standard rates including overtime, premiums, and holiday pay.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Input Form */}
          <div className="space-y-6">
            <div className="bg-[#121212] border border-white/5 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <h2 className="font-semibold text-white text-lg">Rate Details</h2>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-white/80 mb-2 block">Base Day Rate (£)</Label>
                  <Input
                    type="number"
                    value={formData.base_rate}
                    onChange={(e) => setFormData({ ...formData, base_rate: parseFloat(e.target.value) || 0 })}
                    className="bg-[#0A0A0A] border-white/10 text-white h-12 text-lg"
                    data-testid="chit-base-rate"
                  />
                </div>
                <div>
                  <Label className="text-white/80 mb-2 block">Holiday Pay %</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.holiday_pay_percent}
                    onChange={(e) => setFormData({ ...formData, holiday_pay_percent: parseFloat(e.target.value) || 0 })}
                    className="bg-[#0A0A0A] border-white/10 text-white h-12 text-lg"
                    data-testid="chit-holiday-pay"
                  />
                </div>
              </div>
            </div>

            <div className="bg-[#121212] border border-white/5 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <h2 className="font-semibold text-white text-lg">Working Hours</h2>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-white/80 mb-2 block">Call Time</Label>
                  <Input
                    type="time"
                    value={formData.call_time}
                    onChange={(e) => setFormData({ ...formData, call_time: e.target.value })}
                    className="bg-[#0A0A0A] border-white/10 text-white h-12 text-lg"
                    data-testid="chit-call-time"
                  />
                </div>
                <div>
                  <Label className="text-white/80 mb-2 block">Wrap Time</Label>
                  <Input
                    type="time"
                    value={formData.wrap_time}
                    onChange={(e) => setFormData({ ...formData, wrap_time: e.target.value })}
                    className="bg-[#0A0A0A] border-white/10 text-white h-12 text-lg"
                    data-testid="chit-wrap-time"
                  />
                </div>
              </div>
            </div>

            <div className="bg-[#121212] border border-white/5 rounded-2xl p-6">
              <h2 className="font-semibold text-white text-lg mb-6">Additional Options</h2>
              
              <div className="space-y-4">
                <label className="flex items-center gap-4 p-3 bg-[#0A0A0A] rounded-lg cursor-pointer hover:bg-white/5 transition-colors">
                  <Checkbox
                    checked={formData.lunch_break}
                    onCheckedChange={(c) => setFormData({ ...formData, lunch_break: c })}
                    className="w-5 h-5 data-[state=checked]:bg-[#D4AF37] data-[state=checked]:border-[#D4AF37]"
                  />
                  <div>
                    <span className="text-white block">Lunch Break</span>
                    <span className="text-white/40 text-sm">1 hour deducted after 6 hours</span>
                  </div>
                </label>
                
                <label className="flex items-center gap-4 p-3 bg-[#0A0A0A] rounded-lg cursor-pointer hover:bg-white/5 transition-colors">
                  <Checkbox
                    checked={formData.early_call}
                    onCheckedChange={(c) => setFormData({ ...formData, early_call: c })}
                    className="w-5 h-5 data-[state=checked]:bg-[#D4AF37] data-[state=checked]:border-[#D4AF37]"
                  />
                  <div>
                    <span className="text-white block">Early Call</span>
                    <span className="text-white/40 text-sm">Before 7am - adds 10% premium</span>
                  </div>
                </label>
                
                <label className="flex items-center gap-4 p-3 bg-[#0A0A0A] rounded-lg cursor-pointer hover:bg-white/5 transition-colors">
                  <Checkbox
                    checked={formData.night_shoot}
                    onCheckedChange={(c) => setFormData({ ...formData, night_shoot: c })}
                    className="w-5 h-5 data-[state=checked]:bg-[#D4AF37] data-[state=checked]:border-[#D4AF37]"
                  />
                  <div>
                    <span className="text-white block">Night Shoot</span>
                    <span className="text-white/40 text-sm">Adds 15% premium</span>
                  </div>
                </label>
              </div>
            </div>

            <Button
              onClick={calculateChit}
              disabled={loading}
              className="w-full gold-gradient text-black font-bold py-4 text-lg uppercase tracking-wider hover:brightness-110"
              data-testid="calculate-chit-btn"
            >
              <Calculator className="w-5 h-5 mr-2" />
              {loading ? 'Calculating...' : 'Calculate Pay'}
            </Button>
          </div>

          {/* Results */}
          <div className="lg:sticky lg:top-8 h-fit">
            <div className="bg-[#121212] border border-white/5 rounded-2xl p-8">
              <h2 className="font-['Playfair_Display'] text-2xl font-bold text-white mb-8 text-center">
                Pay Summary
              </h2>
              
              {result ? (
                <div className="space-y-4">
                  <div className="flex justify-between py-4 border-b border-white/10">
                    <span className="text-white/60">Base Pay (10 hr day)</span>
                    <span className="text-white font-semibold text-lg">£{result.base_pay.toFixed(2)}</span>
                  </div>
                  
                  <div className="flex justify-between py-4 border-b border-white/10">
                    <div>
                      <span className="text-white/60 block">Hours Worked</span>
                      <span className="text-white/40 text-sm">{result.breakdown?.hours_worked} hours total</span>
                    </div>
                    <span className="text-white/60">{result.breakdown?.hours_worked} hrs</span>
                  </div>
                  
                  {result.breakdown?.overtime_hours > 0 && (
                    <div className="flex justify-between py-4 border-b border-white/10">
                      <div>
                        <span className="text-white/60 block">Overtime</span>
                        <span className="text-white/40 text-sm">{result.breakdown?.overtime_hours} hrs @ 1.5x</span>
                      </div>
                      <span className="text-[#D4AF37] font-semibold text-lg">£{result.overtime_pay.toFixed(2)}</span>
                    </div>
                  )}
                  
                  {result.breakdown?.early_call_premium > 0 && (
                    <div className="flex justify-between py-4 border-b border-white/10">
                      <span className="text-white/60">Early Call Premium (10%)</span>
                      <span className="text-[#D4AF37] font-semibold text-lg">£{result.breakdown.early_call_premium.toFixed(2)}</span>
                    </div>
                  )}
                  
                  {result.breakdown?.night_premium > 0 && (
                    <div className="flex justify-between py-4 border-b border-white/10">
                      <span className="text-white/60">Night Shoot Premium (15%)</span>
                      <span className="text-[#D4AF37] font-semibold text-lg">£{result.breakdown.night_premium.toFixed(2)}</span>
                    </div>
                  )}
                  
                  <div className="flex justify-between py-4 border-b border-white/10">
                    <span className="text-white/60">Holiday Pay ({formData.holiday_pay_percent}%)</span>
                    <span className="text-white font-semibold text-lg">£{result.holiday_pay.toFixed(2)}</span>
                  </div>
                  
                  <div className="flex justify-between items-center pt-6 border-t-2 border-[#D4AF37]">
                    <span className="text-white font-bold text-xl">GROSS TOTAL</span>
                    <span className="text-[#D4AF37] font-['Playfair_Display'] text-4xl font-bold">
                      £{result.gross_total.toFixed(2)}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="text-center py-16">
                  <div className="w-20 h-20 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mx-auto mb-4">
                    <Calculator className="w-10 h-10 text-[#D4AF37]/50" />
                  </div>
                  <p className="text-white/40">Enter your details and click Calculate</p>
                </div>
              )}
            </div>

            {/* Info Box */}
            <div className="mt-6 bg-[#0A0A0A] border border-white/5 rounded-xl p-4">
              <p className="text-white/40 text-sm">
                <strong className="text-white/60">Note:</strong> This calculator uses UK film industry standard rates. 
                A standard day is 10 hours. Overtime is calculated at 1.5x the hourly rate. 
                Holiday pay of 12.07% is the standard accrued entitlement.
              </p>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/5 py-8 mt-12">
        <div className="max-w-6xl mx-auto px-6 text-center text-white/40 text-sm">
          © 2026 2nd Team Casting. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default ChitCalculatorPage;
